import { NpsScore, NpsScoreBucket, NpsStatus } from "@/types/nps";

export const NPS_MAX_FEEDBACK_LENGTH = 1024;
export const NPS_PLACEHOLDER_FEEDBACK = new Set(["x", "submit"]);

export const NPS_CLOSE_DEFER_SECONDS = 60 * 60 * 24 * 3; // 3 days
export const NPS_SUBMITTED_DEFER_SECONDS = 60 * 60 * 24 * 30; // 30 days

export function normalizeNpsDate(value: string | Date | undefined): string {
  const date = value ? new Date(value) : new Date();

  if (Number.isNaN(date.getTime())) {
    throw new Error("Invalid NPS response date");
  }

  return date.toISOString().slice(0, 10);
}

export function normalizeNpsScore(score: number | null | undefined): NpsScore | null {
  if (score === null || score === undefined) {
    return null;
  }

  if (!Number.isInteger(score) || score < 0 || score > 10) {
    throw new Error("NPS score must be an integer from 0 through 10");
  }

  return score as NpsScore;
}

export function normalizeNpsFeedback(feedback: string | null | undefined): string | undefined {
  const trimmed = feedback?.trim();

  if (!trimmed || NPS_PLACEHOLDER_FEEDBACK.has(trimmed.toLowerCase())) {
    return undefined;
  }

  return trimmed.slice(0, NPS_MAX_FEEDBACK_LENGTH);
}

export function getNpsScoreBucket(score: NpsScore): NpsScoreBucket {
  if (score >= 9) {
    return "promoter";
  }

  if (score >= 7) {
    return "passive";
  }

  return "detractor";
}

export function getNpsSubmittedDeferSeconds(): number {
  return NPS_SUBMITTED_DEFER_SECONDS;
}

export function inferNpsStatus(score: NpsScore | null, feedback?: string): NpsStatus {
  if (score === null) {
    return "started";
  }

  return feedback ? "completed" : "voted";
}

export function getNpsDateRange(
  days: number,
  end: Date = new Date()
): { startDate: string; endDate: string } {
  const endDate = normalizeNpsDate(end);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (days - 1));

  return {
    startDate: normalizeNpsDate(start),
    endDate,
  };
}

export function getNpsDateRangeDays(startDate: string, endDate: string): string[] {
  const start = new Date(`${startDate}T00:00:00.000Z`);
  const end = new Date(`${endDate}T00:00:00.000Z`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return [];
  }

  const dates: string[] = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    dates.push(normalizeNpsDate(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return dates;
}
